import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { computeRollFreeUp, type NativeControlServices } from '@/plugins/control/NativeControlUtils'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import type { EngineContext } from '@/runtime/EngineContext'
import { type PhaseRenderContext, RenderPhase } from '@/shared/types/assets'
import type {
  CharacterAnimationController,
  CharacterAnimationControllerFactory,
  CharacterAnimationUpdateContext,
  CharacterPresentationFrame,
  CharacterPresenter,
  CharacterPresenterFactory,
  CharacterSnapshot,
} from '@/shared/types/character'
import { CameraMode } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  CameraRig,
  ControlManager,
  ControlModeFactory,
  ControlModeRegistry,
  PointerLockService,
  RenderActivityContext,
  RenderActivityReport,
  RenderContributor,
  RenderPipeline,
  ViewEventRouter,
  ViewRegistry,
} from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext } from '@/shared/types/view'
import { CharacterServiceImpl } from './CharacterService'
import { CharacterFirstPersonController } from './control/CharacterFirstPersonController'
import { CharacterThirdPersonController } from './control/CharacterThirdPersonController'
import { createAimDirection } from './control/characterControlUtils'
import { CapsuleCharacterPresenter } from './presentation/CapsuleCharacterPresenter'

/** Options for configuring the character runtime plugin. */
export interface CharacterPluginOptions {
  /** Creates the presenter used to render a character. */
  presenterFactory?: CharacterPresenterFactory
  /** Creates an optional animation controller for the presenter. */
  animationControllerFactory?: CharacterAnimationControllerFactory
}

interface CharacterPresentationRuntime {
  presenter: CharacterPresenter
  animationController: CharacterAnimationController | null
  visible: boolean
}

interface CharacterCameraActivityController {
  hasCameraActivity(): boolean
}

const WORLD_UP = new THREE.Vector3(0, 0, 1)
const WORLD_FORWARD = new THREE.Vector3(1, 0, 0)
const ORIGIN = new THREE.Vector3()

/** Character runtime plugin. */
export class CharacterPlugin implements Plugin {
  /** Plugin registry name. */
  readonly name = 'character'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** Plugin startup priority. */
  readonly priority = 18
  /** Services required before this plugin starts. */
  readonly dependencies = [
    'ViewRegistry',
    'ViewEventRouter',
    'PointerLockService',
    'CameraRig',
    'ViewportCamera',
    'ControlModeRegistry',
    'RenderPipeline',
    'SpatialQueryService',
  ] as const
  /** Services registered by this plugin. */
  readonly provides = ['CharacterService'] as const

  private readonly presenterFactory: CharacterPresenterFactory
  private readonly animationControllerFactory: CharacterAnimationControllerFactory | null
  private context: EngineContext | null = null
  private service: CharacterServiceImpl | null = null
  private viewRegistry: ViewRegistry | null = null
  private viewEventRouter: ViewEventRouter | null = null
  private pointerLockService: PointerLockService | null = null
  private controlModeRegistry: ControlModeRegistry | null = null
  private renderPipeline: RenderPipeline | null = null
  private characterSceneRoot: THREE.Group | null = null
  private characterDebugRoot: THREE.Group | null = null
  private readonly isolatedCharacterScene = new THREE.Scene()
  private readonly presentationRuntimes = new Map<string, CharacterPresentationRuntime>()
  private disposed = false
  private readonly renderContributor: RenderContributor = {
    kind: 'character',
    graphicsRequirements: DUAL_BACKEND_GRAPHICS_REQUIREMENTS,
    phases: [RenderPhase.MainOcclusionTested],
    renderPriority: 10,
    render: (frame, _phase, renderContext) => {
      this.renderCharacters(frame, renderContext)
    },
    getRenderActivity: (context) => this.getRenderActivity(context),
  }
  private readonly handleViewRemoved = ({ viewId }: { viewId: string }) => {
    this.service?.unbindViewCharacter(viewId)
  }

  constructor(options: CharacterPluginOptions = {}) {
    this.presenterFactory =
      options.presenterFactory ??
      ((context) =>
        new CapsuleCharacterPresenter({
          characterId: context.characterId,
          sceneRoot: context.sceneRoot,
          debugRoot: context.debugRoot,
        }))
    this.animationControllerFactory = options.animationControllerFactory ?? null
  }

  /**
   * Registers the character service.
   *
   * @param context - Engine context used for service registration.
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.disposed = false
    this.service = new CharacterServiceImpl((reason) => this.invalidateCharacter(reason))
    context.registerService('CharacterService', this.service)
  }

  /** Starts control bindings, render contribution, and scene roots. */
  onStart(): void {
    if (!this.context || !this.service) {
      throw new Error('CharacterPlugin is not initialized')
    }

    this.viewRegistry = this.context.getService('ViewRegistry')
    this.viewEventRouter = this.context.getService('ViewEventRouter')
    this.pointerLockService = this.context.getService('PointerLockService')
    this.context.getService('CameraRig')
    this.context.getService('ViewportCamera')
    this.controlModeRegistry = this.context.getService('ControlModeRegistry')
    this.renderPipeline = this.context.getService('RenderPipeline')
    this.service.setSpatialQueryService(this.context.getService('SpatialQueryService'))
    this.initializeSceneRoots()

    const firstPersonFactory: ControlModeFactory<NativeControlServices> = (services) =>
      new CharacterFirstPersonController({
        services,
        characterService: this.service!,
        viewEventRouter: this.viewEventRouter!,
        pointerLockService: this.pointerLockService!,
        onVisualInput: (reason) => this.invalidateCharacter(reason),
      })
    const thirdPersonFactory: ControlModeFactory<NativeControlServices> = (services) =>
      new CharacterThirdPersonController({
        services,
        characterService: this.service!,
        viewEventRouter: this.viewEventRouter!,
        onVisualInput: (reason) => this.invalidateCharacter(reason),
      })
    this.controlModeRegistry.registerFactory('character-first-person', firstPersonFactory)
    this.controlModeRegistry.registerFactory('character-third-person', thirdPersonFactory)
    this.renderPipeline.addRenderContributor(this.renderContributor)
    this.context.events.on('view.removed', this.handleViewRemoved)
    this.syncPresentationRuntimes()
  }

  /**
   * Updates character simulation and presentation state for a frame.
   *
   * @param frame - Engine frame context.
   */
  onUpdateFrame(frame: EngineFrameContext): void {
    this.service?.update(frame.delta)
    this.syncPresentationRuntimes({
      frameNumber: frame.frameNumber,
      timestamp: frame.timestamp,
      delta: frame.delta,
    })
  }

  /**
   * Applies character camera state before a view is rendered.
   *
   * @param frame - View frame context.
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!this.service || !this.viewRegistry) {
      return
    }

    this.syncPresentationRuntimes()
    this.applyPresentationVisibility(frame.viewId)

    const binding = this.service.getViewBinding(frame.viewId)
    if (!binding) {
      return
    }

    const controlManager = this.viewRegistry.getExtension(frame.viewId, 'controlManager') as
      | ControlManager
      | undefined
    const activeMode = controlManager?.activeMode
    if (activeMode !== 'character-first-person' && activeMode !== 'character-third-person') {
      return
    }

    const rig = this.viewRegistry.getExtension(frame.viewId, 'cameraRig') as CameraRig | undefined
    if (!rig) {
      return
    }

    const handle = this.service.get(binding.characterId)
    if (!handle) {
      return
    }

    const snapshot = handle.getSnapshot()
    if (snapshot.placementPending) return
    if (activeMode === 'character-first-person') {
      this.applyFirstPersonCamera(rig, snapshot)
      return
    }

    const thirdPersonController = controlManager?.activeController
    if (!hasThirdPersonCameraState(thirdPersonController)) {
      return
    }
    this.applyThirdPersonCamera(rig, thirdPersonController.getCameraState(snapshot, frame.delta))
  }

  /** Releases registered services, scene roots, and control factories. */
  onDestroy(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    this.context?.events.off('view.removed', this.handleViewRemoved)
    this.renderPipeline?.removeRenderContributor(this.renderContributor)
    this.controlModeRegistry?.unregisterFactory('character-third-person')
    this.controlModeRegistry?.unregisterFactory('character-first-person')
    this.disposePresentationRuntimes()
    this.characterSceneRoot?.removeFromParent()
    this.characterDebugRoot?.removeFromParent()
    this.service?.dispose()
    this.service = null
    this.viewRegistry = null
    this.viewEventRouter = null
    this.pointerLockService = null
    this.controlModeRegistry = null
    this.renderPipeline = null
    this.characterSceneRoot = null
    this.characterDebugRoot = null
    this.context = null
  }

  private initializeSceneRoots(): void {
    if (!this.context) {
      return
    }

    const characterRoot = this.context.sceneGraph.registerRoot('character')
    this.characterSceneRoot = new THREE.Group()
    this.characterSceneRoot.name = 'characterRuntimeRoot'
    characterRoot.add(this.characterSceneRoot)

    this.characterDebugRoot = new THREE.Group()
    this.characterDebugRoot.name = 'characterRuntimeDebugRoot'
    this.characterDebugRoot.visible = false
    this.context.sceneGraph.debugRoot.add(this.characterDebugRoot)
  }

  private invalidateCharacter(reason: string): void {
    this.context?.renderInvalidation?.invalidate(reason)
  }

  private getRenderActivity(context: RenderActivityContext): RenderActivityReport {
    if (context.renderedViewIds.length === 0 || !this.service) {
      return { needsNextFrame: false, reasons: [] }
    }

    const reasons = new Set<string>()
    for (const handle of this.service.list()) {
      const command = handle.getCommand()
      const snapshot = handle.getSnapshot()
      if (snapshot.collisionWaiting) continue
      if (Math.abs(command.move.forward) > 0 || Math.abs(command.move.right) > 0 || command.jump) {
        reasons.add('character.motion')
      }
      if (
        snapshot.speed > 0.001 ||
        snapshot.planarSpeed > 0.001 ||
        snapshot.locomotionState === 'jump' ||
        snapshot.locomotionState === 'fall' ||
        snapshot.locomotionState === 'land'
      ) {
        reasons.add('character.motion')
      }
    }
    for (const viewId of context.renderedViewIds) {
      if (this.hasThirdPersonCameraActivity(viewId)) {
        reasons.add('character.cameraFollow')
      }
    }

    return {
      needsNextFrame: reasons.size > 0,
      reasons: Array.from(reasons),
    }
  }

  private syncPresentationRuntimes(animationContext?: CharacterAnimationUpdateContext): void {
    if (!this.service || !this.characterSceneRoot || !this.characterDebugRoot) {
      return
    }

    const activeIds = new Set<string>()
    for (const handle of this.service.list()) {
      activeIds.add(handle.id)
      const snapshot = handle.getSnapshot()
      if (snapshot.placementPending) continue
      const runtime = this.ensurePresentationRuntime(handle.id)
      const frame = this.createPresentationFrame(snapshot)
      runtime.presenter.update(frame)
      if (animationContext) {
        runtime.animationController?.update(frame, animationContext)
      }
    }

    for (const [characterId] of this.presentationRuntimes) {
      if (!activeIds.has(characterId)) {
        this.disposePresentationRuntime(characterId)
      }
    }
  }

  private ensurePresentationRuntime(characterId: string): CharacterPresentationRuntime {
    const existing = this.presentationRuntimes.get(characterId)
    if (existing) {
      return existing
    }

    if (!this.characterSceneRoot || !this.characterDebugRoot) {
      throw new Error('CharacterPlugin scene roots are not initialized')
    }

    const presenter = this.presenterFactory({
      characterId,
      sceneRoot: this.characterSceneRoot,
      debugRoot: this.characterDebugRoot,
    })
    const animationController =
      this.animationControllerFactory?.({
        characterId,
        presenter,
      }) ?? null
    const runtime: CharacterPresentationRuntime = {
      presenter,
      animationController,
      visible: true,
    }
    this.presentationRuntimes.set(characterId, runtime)
    this.invalidateCharacter('character.presentationReady')
    return runtime
  }

  private disposePresentationRuntime(characterId: string): void {
    const runtime = this.presentationRuntimes.get(characterId)
    if (!runtime) {
      return
    }

    runtime.animationController?.dispose()
    runtime.presenter.dispose()
    this.presentationRuntimes.delete(characterId)
  }

  private disposePresentationRuntimes(): void {
    for (const characterId of Array.from(this.presentationRuntimes.keys())) {
      this.disposePresentationRuntime(characterId)
    }
  }

  private applyPresentationVisibility(viewId: string): void {
    if (!this.service || !this.viewRegistry) {
      return
    }

    const binding = this.service.getViewBinding(viewId)
    const controlManager = this.viewRegistry.getExtension(viewId, 'controlManager') as
      | ControlManager
      | undefined
    const hiddenCharacterId =
      binding && controlManager?.activeMode === 'character-first-person'
        ? binding.characterId
        : null

    for (const [characterId, runtime] of this.presentationRuntimes) {
      const visible = characterId !== hiddenCharacterId
      runtime.presenter.setVisible(visible)
      if (runtime.visible !== visible) {
        runtime.visible = visible
        this.invalidateCharacter('character.presentationVisibility')
      }
    }
  }

  private hasThirdPersonCameraActivity(viewId: string): boolean {
    if (!this.service || !this.viewRegistry) {
      return false
    }
    const binding = this.service.getViewBinding(viewId)
    if (!binding) {
      return false
    }
    if (this.service.get(binding.characterId)?.getSnapshot().placementPending) return false
    const controlManager = this.viewRegistry.getExtension(viewId, 'controlManager') as
      | ControlManager
      | undefined
    if (controlManager?.activeMode !== 'character-third-person') {
      return false
    }
    const controller = controlManager.activeController
    return hasCharacterCameraActivity(controller) && controller.hasCameraActivity()
  }

  private createPresentationFrame(snapshot: CharacterSnapshot): CharacterPresentationFrame {
    const aimDirection = createAimDirection(snapshot.aimYaw, snapshot.aimPitch)
    const bodyQuaternion = new THREE.Quaternion().setFromAxisAngle(WORLD_UP, snapshot.bodyYaw)
    const bodyDirection = WORLD_FORWARD.clone().applyQuaternion(bodyQuaternion).normalize()
    const capsuleAxis = snapshot.capsule.end.clone().sub(snapshot.capsule.start)
    const capsuleDirection =
      capsuleAxis.lengthSq() > Number.EPSILON ? capsuleAxis.normalize() : WORLD_UP.clone()
    const eyeQuaternion = quaternionFromLookDirection(aimDirection)

    return {
      characterId: snapshot.id,
      snapshot,
      anchors: {
        root: {
          position: snapshot.floorPoint.clone(),
          quaternion: bodyQuaternion.clone(),
        },
        capsule: {
          position: snapshot.capsule.start.clone().add(snapshot.capsule.end).multiplyScalar(0.5),
          quaternion: new THREE.Quaternion().setFromUnitVectors(WORLD_UP, capsuleDirection),
        },
        eye: {
          position: snapshot.eyePosition.clone(),
          quaternion: eyeQuaternion,
        },
      },
      bodyDirection,
      aimDirection,
      locomotionState: snapshot.locomotionState,
      speed: snapshot.speed,
      planarSpeed: snapshot.planarSpeed,
    }
  }

  private applyFirstPersonCamera(rig: CameraRig, snapshot: CharacterSnapshot): void {
    const direction = createAimDirection(snapshot.aimYaw, snapshot.aimPitch)
    const state = rig.getState()
    const position = snapshot.eyePosition.clone()
    rig.setState({
      ...state,
      position,
      target: position.clone().add(direction),
      up: computeRollFreeUp(direction),
      mode: CameraMode.Perspective,
    })
  }

  private applyThirdPersonCamera(
    rig: CameraRig,
    cameraState: {
      position: THREE.Vector3
      target: THREE.Vector3
      up: THREE.Vector3
    }
  ): void {
    const state = rig.getState()
    rig.setState({
      ...state,
      position: cameraState.position.clone(),
      target: cameraState.target.clone(),
      up: cameraState.up.clone(),
      mode: CameraMode.Perspective,
    })
  }

  private renderCharacters(frame: ViewFrameContext, renderContext: PhaseRenderContext): void {
    if (!this.characterSceneRoot || this.characterSceneRoot.children.length === 0) {
      return
    }

    // 角色挂在独立 Scene 上以隔离主场景；viewport/scissor、target 与 renderer 状态由 backend 依当前 View 决定。
    const restoreParent = this.attachCharacterRootToIsolatedScene()
    try {
      submitSceneInView(renderContext, this.isolatedCharacterScene, frame.camera)
    } finally {
      restoreParent()
    }
  }

  private attachCharacterRootToIsolatedScene(): () => void {
    if (!this.characterSceneRoot) {
      return () => {}
    }

    const originalParent = this.characterSceneRoot.parent
    this.isolatedCharacterScene.add(this.characterSceneRoot)
    return () => {
      this.characterSceneRoot?.removeFromParent()
      if (originalParent) {
        originalParent.add(this.characterSceneRoot!)
      }
    }
  }
}

function hasThirdPersonCameraState(
  value: unknown
): value is Pick<CharacterThirdPersonController, 'getCameraState'> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'getCameraState' in value &&
    typeof value.getCameraState === 'function'
  )
}

function hasCharacterCameraActivity(
  controller: unknown
): controller is CharacterCameraActivityController {
  return (
    typeof controller === 'object' &&
    controller !== null &&
    'hasCameraActivity' in controller &&
    typeof controller.hasCameraActivity === 'function'
  )
}

function quaternionFromLookDirection(direction: THREE.Vector3): THREE.Quaternion {
  const up = computeRollFreeUp(direction)
  const matrix = new THREE.Matrix4().lookAt(ORIGIN, direction.clone(), up).invert()
  return new THREE.Quaternion().setFromRotationMatrix(matrix)
}
